// @flow

import invariant from 'invariant';
import { type RequestContext } from 'app/common/lib/RequestContext';

// String to bool helper matching python distutils.util.strtobool
// https://docs.python.org/2/distutils/apiref.html#distutils.util.strtobool
function _strToBool(field: string): boolean {
  switch (field.toLowerCase()) {
    case '1':
    case 't':
    case 'true':
    case 'y':
    case 'yes':
      return true;
    default:
      return false;
  }
}

class ExperimentChecker {
  requestContext: RequestContext;
  experimentName: string;

  constructor(requestContext: RequestContext, experimentName: string) {
    invariant(requestContext, 'You must pass a requestContext to check an Experiment');
    invariant(experimentName, 'You must pass the name of the experiment to check');
    this.requestContext = requestContext;
    this.experimentName = experimentName.toLowerCase();
  }

  _isSeoExperiment(): boolean {
    return this.experimentName.startsWith('seo_');
  }

  _getGroup(activate: boolean): string {
    if (this.requestContext.isBot) {
      if (!this._isSeoExperiment()) {
        return '';
      }

      // TODO: Warn if activate is not true?
      // True is the default, but you can't disable logging for seo experiments, so don't ask to disable it.
      return this.requestContext.seoExperiments.v2GetSEOGroup(this.experimentName) || '';
    } else {
      if (this._isSeoExperiment()) {
        if (activate) {
          return '';
        } else {
          return this.requestContext.seoExperiments.v2GetSEOGroup(this.experimentName) || '';
        }
      }

      if (activate) {
        return this.requestContext.experiments.v2ActivateExperiment(this.experimentName) || '';
      } else {
        return this.requestContext.experiments.v2GetGroup(this.experimentName) || '';
      }
    }
  }
}

export class ExperimentReader extends ExperimentChecker {
  getGroup(): string {
    return this._getGroup(false);
  }
}

export class SingleTreatmentExp extends ExperimentChecker {
  _isTreatment(group: string): boolean {
    return group.startsWith('enabled') || group.startsWith('employee');
  }

  enabled(): boolean {
    return this._isTreatment(this._getGroup(true));
  }

  enabledWithNoActivation(): boolean {
    return this._isTreatment(this._getGroup(false));
  }
}

export class MultiTreatmentExp extends ExperimentChecker {
  check(group: string | null): boolean {
    return this._getGroup(true) === group;
  }

  checkWithNoActivation(group: string | null): boolean {
    return this._getGroup(false) === group;
  }
}

type GroupValues = { [key: string | number]: string };

export class MultiVariableExp extends ExperimentChecker {
  fieldNames: Array<string | number>;

  constructor(
    requestContext: RequestContext,
    experimentName: string,
    fieldNames: ?Array<string | number> = null,
  ) {
    super(requestContext, experimentName);

    this.fieldNames = fieldNames || [];
  }

  _groupToValues(group: string): GroupValues {
    const parts = group.split('_');
    const params = {};
    parts.forEach((part, index) => {
      params[index] = part;
    });
    if (this.fieldNames.length) {
      parts.forEach((part, index) => {
        const fieldName = String(this.fieldNames[index]);
        params[fieldName.toLowerCase() || index] = part;
      });
    }
    return params;
  }

  // Create a method that's easily mocked for tests
  _getParam(activate: boolean, name: string | number): string | null {
    const values = this._groupToValues(this._getGroup(activate));
    const lowerName = String(name).toLowerCase();
    return lowerName in values ? values[lowerName] : null;
  }

  getStringParam(name: string | number): string {
    return String(this._getParam(true, name) || '');
  }

  getIntParam(name: string | number): number {
    return Number(this._getParam(true, name) || 0);
  }

  getBoolParam(name: string | number): boolean {
    return _strToBool(this._getParam(true, name) || 'false');
  }

  getStringParamWithNoActivation(name: string | number): string {
    return String(this._getParam(false, name) || '');
  }

  getIntParamWithNoActivation(name: string | number): number {
    return Number(this._getParam(false, name) || 0);
  }

  getBoolParamWithNoActivation(name: string | number): boolean {
    return _strToBool(this._getParam(false, name) || 'false');
  }
}
